import type { ReactNode } from 'react'
import { AlertIcon, CheckIcon, InfoIcon } from '../ui/icons'

/**
 * DashboardShell status pills. design-system.md §8.
 *
 * 🔴 ICON + COLOUR + TEXT, ALWAYS ALL THREE — and the TYPE enforces it.
 * `icon` and `children` are both required, not optional. Colour never carries
 * meaning alone: that is colour-blind safety and the accessibility pass, and a
 * pill that *can* be constructed without them is a pill that eventually will
 * be.
 */
export type StatusTone = 'success' | 'warning' | 'danger' | 'neutral'

const TONE: Record<StatusTone, { bg: string; fg: string; line: string }> = {
  // All twelve values are tokens — none is retyped here.
  success: {
    bg: 'var(--color-pill-success-bg)',
    fg: 'var(--color-pill-success-fg)',
    line: 'var(--color-pill-success-line)',
  },
  warning: {
    bg: 'var(--color-pill-warning-bg)',
    fg: 'var(--color-pill-warning-fg)',
    line: 'var(--color-pill-warning-line)',
  },
  danger: {
    bg: 'var(--color-pill-danger-bg)',
    fg: 'var(--color-pill-danger-fg)',
    line: 'var(--color-pill-danger-line)',
  },
  neutral: {
    bg: 'var(--color-pill-neutral-bg)',
    fg: 'var(--color-pill-neutral-fg)',
    line: 'var(--color-pill-neutral-line)',
  },
}

export interface StatusPillProps {
  tone: StatusTone
  /** 🔴 Required. 14px. Colour alone is not a status. */
  icon: ReactNode
  /** 🔴 Required. Colour alone is not a status. */
  children: ReactNode
}

export function StatusPill({ tone, icon, children }: StatusPillProps) {
  const t = TONE[tone]
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-pill px-3 py-1.5 font-dash text-dash-xs font-bold"
      style={{ background: t.bg, color: t.fg, border: `1px solid ${t.line}` }}
    >
      <span aria-hidden="true" className="inline-flex shrink-0">
        {icon}
      </span>
      {children}
    </span>
  )
}

/**
 * The default icon for a tone.
 *
 * 🔴 IT DOES NOT MAKE `icon` OPTIONAL, AND THAT IS THE WHOLE POINT. The prop
 * stays required — a pill built without one still fails to compile — but a
 * caller whose only distinction *is* the tone ("Active" / "Suspended") now has
 * one honest answer to reach for instead of picking an icon at random or, as
 * the superadmin screens did, omitting it and shipping colour alone.
 *
 * ⚠️ USE A SPECIFIC ICON WHEN THERE IS ONE. A pill that says "Recording" wants
 * the camera, not a tick. This is the fallback, not the recommendation.
 */
export function toneIcon(tone: StatusTone): ReactNode {
  const Icon =
    tone === 'success' ? CheckIcon : tone === 'neutral' ? InfoIcon : AlertIcon
  return <Icon size={14} />
}
